import {useI18n} from 'vue-i18n'
import {useRoute} from 'vue-router'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {WorkspaceTabKindType} from '@toolify/client/src/modules/workspaceTabs/enum/WorkspaceTabKindType'
import {IWorkspaceTab} from '@toolify/client/src/modules/workspaceTabs/types/IWorkspaceTab'
import {useResourceItemStore} from '@toolify/client/src/stores/ResourceItemStore/useResourceItemStore'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useCollectionStore} from '@toolify/client/src/stores/CollectionStore/useCollectionStore'
import {useDirectConversationStore} from '@toolify/client/src/stores/DirectConversationStore/useDirectConversationStore'
import {ResourceTypeDataMap} from '@toolify/client/src/modules/resources/maps/ResourceTypeDataMap'
import {ResourceTypeIconMap} from '@toolify/client/src/modules/resources/maps/ResourceTypeIconMap'
import {getResourceTypeIconColor} from '@toolify/client/src/modules/resources/functions/getResourceTypeIconColor'
import {getCollectionItemIconNameType} from '@toolify/client/src/modules/collections/functions/getCollectionItemIconNameType'
import {getCollectionItemLabel} from '@toolify/client/src/modules/collections/functions/getCollectionItemLabel'
import {ClientRouteParamType} from '@toolify/client/src/setup/router/enum/ClientRouteParamType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {getTabFromRoute} from '@toolify/client/src/modules/workspaceTabs/functions/getTabFromRoute'
import {
  getResourceCollectionLabel,
  getResourceTypeFromCollectionPath,
} from '@toolify/client/src/modules/resources/functions/getResourceCollectionRoute'
import {IResponseResource} from '@toolify/server/src/services/ResourceService/types/IResponseResource'

export function useWorkspaceTabPresentation() {
  const {t} = useI18n()
  const route = useRoute()
  const resourceItemStore = useResourceItemStore()
  const workspaceStore = useWorkspaceStore()
  const collectionStore = useCollectionStore()
  const directConversationStore = useDirectConversationStore()

  function findResourceItem(tab: IWorkspaceTab) {
    if(!tab.resourceType || !tab.resourceId) {
      return null
    }
    return resourceItemStore.resourceItems?.find(item => {
      return item.resourceEntity.resourceType === tab.resourceType
        && item.resourceEntity.resourceId === tab.resourceId
    }) ?? null
  }

  function getTabTitle(tab: IWorkspaceTab): string {
    if(tab.kind === WorkspaceTabKindType.Resource && tab.resourceType && tab.resourceId) {
      if(tab.resourceType === ResourceType.DirectConversation) {
        const conversation = directConversationStore.mappedDirectConversations?.find(item => item.id === tab.resourceId)
        if(conversation?.name) {
          return conversation.name
        }
      }
      const resourceItem = findResourceItem(tab)
      if(resourceItem?.name) {
        return resourceItem.name
      }
      return t('general.noName')
    }
    if(tab.name === RouteName.WorkspaceHome) {
      return workspaceStore.currentWorkspace?.title || t('quickNavigation.quickNavigationItemName.home')
    }
    if(tab.name === RouteName.WorkspaceResources) {
      return t('resourcesPage.title')
    }
    if(tab.name === RouteName.WorkspaceResourceCollection) {
      const resourceType = getResourceTypeFromCollectionPath(tab.params[ClientRouteParamType.ResourceCollection] || '')
      if(resourceType) {
        return getResourceCollectionLabel(resourceType)
      }
      return t('resourcesPage.title')
    }
    if(tab.name === RouteName.Collections) {
      return t('collections.title')
    }
    if(tab.name === RouteName.CollectionRecents) {
      return t('collections.recents')
    }
    if(tab.name === RouteName.Collection) {
      const collectionItemId = tab.params[ClientRouteParamType.CollectionItemId]
      const collectionItem = collectionStore.collectionItems.find(item => item.id === collectionItemId)
      if(collectionItem) {
        return getCollectionItemLabel(collectionItem, resourceItemStore.resourceItems ?? [])
      }
    }
    return t('general.noName')
  }

  function getTabIconNameType(tab: IWorkspaceTab): IconNameType {
    if(tab.kind === WorkspaceTabKindType.Resource && tab.resourceType && tab.resourceId) {
      const resourceItem = findResourceItem(tab)
      if(resourceItem?.iconNameType) {
        return resourceItem.iconNameType
      }
      const dataFunction = ResourceTypeDataMap.get(tab.resourceType)
      if(dataFunction && resourceItem) {
        return dataFunction(resourceItem.resourceEntity as IResponseResource).iconClass
      }
      return ResourceTypeIconMap.get(tab.resourceType) || IconNameType.LayerGroup
    }
    if(tab.name === RouteName.WorkspaceHome) {
      return IconNameType.House
    }
    if(tab.name === RouteName.WorkspaceResources) {
      return IconNameType.LayerGroup
    }
    if(tab.name === RouteName.WorkspaceResourceCollection) {
      const resourceType = getResourceTypeFromCollectionPath(tab.params[ClientRouteParamType.ResourceCollection] || '')
      if(resourceType) {
        return ResourceTypeIconMap.get(resourceType) || IconNameType.LayerGroup
      }
      return IconNameType.LayerGroup
    }
    if(tab.name === RouteName.CollectionRecents) {
      return IconNameType.Clock
    }
    if(tab.name === RouteName.Collection) {
      const collectionItemId = tab.params[ClientRouteParamType.CollectionItemId]
      const collectionItem = collectionStore.collectionItems.find(item => item.id === collectionItemId)
      if(collectionItem) {
        return getCollectionItemIconNameType(collectionItem, resourceItemStore.resourceItems ?? [])
      }
      return IconNameType.Folder
    }
    return IconNameType.Grid2
  }

  function getTabIconColor(tab: IWorkspaceTab): string | undefined {
    if(tab.kind === WorkspaceTabKindType.Resource && tab.resourceType) {
      return getResourceTypeIconColor(tab.resourceType)
    }
    if(tab.name === RouteName.WorkspaceResourceCollection) {
      const resourceType = getResourceTypeFromCollectionPath(tab.params[ClientRouteParamType.ResourceCollection] || '')
      return getResourceTypeIconColor(resourceType)
    }
    return undefined
  }

  function isTabActive(tab: IWorkspaceTab): boolean {
    return getTabFromRoute(route)?.key === tab.key
  }

  return {
    getTabTitle,
    getTabIconNameType,
    getTabIconColor,
    isTabActive,
  }
}
